import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import type { ListFile } from '@shared/model'
import type { DisciplineFilesFilterParams, DisciplineFilesFilters } from '../model/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

import { getFirstQueryValue, normalizeArrayQuery } from '@shared/lib/routes'

/** Array filters that links from app-main encode as `key[0]=…`. */
export const DISCIPLINE_FILES_ARRAY_QUERY_KEYS: readonly string[] = ['category']

const FILTER_QUERY_KEYS: readonly string[] = ['category', 'from', 'to']

/** The kanban card's "Progress report" link asks for this category; the select does not offer it, but the API searches it. */
const LINKED_ONLY_CATEGORIES: readonly string[] = ['PROGRESS_REPORT']
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/

const toDateTimeValue = (value: unknown): string | null => {
  const raw = getFirstQueryValue(value)
  return raw !== null && DATE_TIME_PATTERN.test(raw) && dayjs(raw).isValid() ? raw : null
}

export const emptyDisciplineFilesFilters = (): DisciplineFilesFilters => ({
  categories: [],
  from: null,
  to: null,
})

/** The filter offers the tutor categories, as the legacy one did; other values are dropped unless a link may ask for them. */
export const queryToDisciplineFilesFilters = (query: Record<string, unknown>, lists: ListFile | null): DisciplineFilesFilters => {
  const categories = normalizeArrayQuery(query.category) ?? []
  const isKnownCategory = (category: string): boolean =>
    !lists || category in lists.category || LINKED_ONLY_CATEGORIES.includes(category)

  return {
    categories: categories.filter(isKnownCategory),
    from: toDateTimeValue(query.from),
    to: toDateTimeValue(query.to),
  }
}

export const getDisciplineFilesFilterParams = (filters: DisciplineFilesFilters): DisciplineFilesFilterParams => ({
  category: filters.categories.length ? filters.categories : undefined,
  from: filters.from || undefined,
  to: filters.to || undefined,
})

/** Every filter key is present, an empty one as `undefined`, so spreading it over the route query clears it. */
export const disciplineFilesFiltersToQuery = (filters: DisciplineFilesFilters): LocationQueryRaw => ({
  ...getDisciplineFilesFilterParams(filters),
})

const toComparable = (value: unknown): string => JSON.stringify(normalizeArrayQuery(value) ?? [])

export const getCanonicalDisciplineFilesQuery = (
  query: LocationQuery,
  filters: DisciplineFilesFilters,
): LocationQueryRaw | null => {
  const canonical = disciplineFilesFiltersToQuery(filters)
  const isCanonical = FILTER_QUERY_KEYS.every(key => toComparable(query[key]) === toComparable(canonical[key]))

  return isCanonical ? null : { ...query, ...canonical }
}

/** The API takes `from` and `to` independently, so a lone end of the range is kept. */
export const toDisciplineFilesDateRange = (from: string | null, to: string | null): Date[] | null => {
  const fromDate = from ? dayjs(from) : null
  const toDate = to ? dayjs(to) : null

  if (!fromDate?.isValid()) return null

  return toDate?.isValid() ? [fromDate.toDate(), toDate.toDate()] : [fromDate.toDate()]
}

export const fromDisciplineFilesDateRange = (
  value: (Date | null | undefined)[] | null,
): Pick<DisciplineFilesFilters, 'from' | 'to'> => {
  const toBound = (date: Date | null | undefined): string | null =>
    date && dayjs(date).isValid() ? dayjs(date).format(DateFormat.Default) : null

  return {
    from: toBound(value?.[0]),
    to: toBound(value?.[1]),
  }
}
